import {useEffect, useMemo, useRef} from 'react'

import {
  getState,
  updateForm,
  formsSelector,
  useSelector,
  useAutoForm,
} from '../../store.js'
import {filterOptions} from '../tokenizeOptions.js'
import DropdownItem from './Dropdown/DropdownItem.js'
import {useZropdown} from './Zropdown.js'
import FilterDropdown from './FilterDropdown.js'
import {
  contactOptionsSelector,
  contactOptionTokensSelector,
  deleteContact,
} from '../../data/contacts.js'
import {currentDropdownSelector} from '../../redux/selectors/ui/index.js'
import Icon from '../icons/Icon.js'
import {showAddEditContactModal} from '../../ordoro/Settings/Contacts/AddEditContactModal.js'
import {SelectButton} from './Zelect.js'
import className from '../className.js'
import useStable from '../useStable.js'

export function ContactsDropdownItem({
  option,
  isSelected,
  isFocused,
  onSelect,
}) {
  const contact = option.entity

  return (
    <DropdownItem>
      <div
        className={className`wrap--dropdown-item flex--justify-nowrap label--selectable label--selectable-pt-pb-0 margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
        }} ${{
          selected: isSelected,
        }}`}
      >
        <button
          className="btn--link list__link--dropdown w-75 word-break"
          type="button"
          onClick={onSelect}
        >
          <div className="btn__text">
            {contact ? (
              <>
                <div>{contact.nickname}</div>
                <div>{contact.address.email}</div>
              </>
            ) : (
              option.display
            )}
          </div>
        </button>
        {contact && (
          <span className="wrap--additional-buttons padding-right-0">
            <button
              className="btn btn--link no-underline margin-right-3"
              type="button"
              title="Edit Contact"
              onClick={() => showAddEditContactModal(contact.id)}
            >
              <Icon name="pencil" className="fs-00" />
            </button>
            <button
              className="btn btn--link no-underline"
              type="button"
              title="Delete Contact"
              onClick={() => deleteContact(contact.id)}
            >
              <Icon name="trash" className="fs-00" />
            </button>
          </span>
        )}
      </div>
    </DropdownItem>
  )
}

export function filterContacts(
  dropdown,
  contactOptions,
  contactOptionTokens,
  excludeContactIDs = [],
) {
  try {
    const form = formsSelector(getState())[dropdown]

    if (!form) {
      return
    }

    if (!form.text) {
      updateForm(dropdown, {
        results: [
          ...contactOptions.filter(
            ({entity}) => !entity || !excludeContactIDs.includes(entity.id),
          ),
        ],
        hasFoundNothing: false,
      })

      return
    }

    const results = filterOptions(form.text, contactOptionTokens).filter(
      ({entity}) => !entity || !excludeContactIDs.includes(entity.id),
    )

    updateForm(dropdown, {
      results,
      selectedIndex: 0,
      hasFoundNothing: results.length === 0,
    })
  } catch (err) {
    updateForm(dropdown, {
      serverError: err.message,
    })
  }
}

export default function ContactFilter({
  id,
  excludeContactIDs,
  value,
  onSelect,
  label,
  labelClassName,
  placeholder,
  required,
  disabled,
  buttonClassName,
  errorMessage,
  allowEmpty,
}) {
  placeholder ||= 'Select Contact'
  const dropdown = id || 'contact_filter'

  const form = useAutoForm(dropdown, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    serverError: null,
  })

  let contactOptions = useSelector(contactOptionsSelector)
  if (allowEmpty) {
    contactOptions = [{value: null, display: placeholder}, ...contactOptions]
  }
  contactOptions = useStable(contactOptions)

  const contactOptionTokens = useSelector(contactOptionTokensSelector)

  const chosenOptions = useMemo(
    () => contactOptions.filter((option) => option.value === value),
    [value, contactOptions],
  )

  useEffect(() => {
    filterContacts(
      dropdown,
      contactOptions,
      contactOptionTokens,
      excludeContactIDs,
    )
  }, [contactOptions])

  const buttonRef = useRef()

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onChange: () =>
        filterContacts(
          dropdown,
          contactOptions,
          contactOptionTokens,
          excludeContactIDs,
        ),
      onSelect,
    })

  const currentDropdown = useSelector(currentDropdownSelector)

  return (
    <>
      {label && (
        <label
          className={className`label--bold ${labelClassName}`}
          htmlFor={dropdown}
        >
          <span>{label}</span>
          {required && <span className="required">*</span>}
        </label>
      )}
      <div
        className={`wrap-outer--tag-dropdown-with-filter wrap--dropdown-contacts ${
          currentDropdown === dropdown ? 'open' : ''
        }`}
        data-dropdown={dropdown}
      >
        <SelectButton
          ref={buttonRef}
          dropdown={dropdown}
          className={buttonClassName}
          placeholder={placeholder}
          disabled={disabled}
          errorMessage={errorMessage}
          chosenOptions={chosenOptions}
        />
        <FilterDropdown
          dropdown={dropdown}
          dropdownClassName="list--dropdown-contacts"
          form={form}
          onChangeInternal={onChangeInternal}
          onConfirm={onConfirm}
          onUp={onUp}
          onDown={onDown}
          placeholder="Search Contacts"
          includeSearchInput
          parentRef={buttonRef}
          footer={
            <div className="list__item list__item--dropdown list__item--dropdown-last">
              <button
                className="btn--link list__link list__link--dropdown list__link--contact-save"
                type="button"
                onClick={() => showAddEditContactModal()}
              >
                Create a Contact
              </button>
            </div>
          }
        >
          <>
            {form.results.map((option, index) => (
              <ContactsDropdownItem
                key={option.value}
                option={option}
                onSelect={() => onSelectInternal(option)}
                isSelected={option.value === value}
                isFocused={index === form.selectedIndex}
              />
            ))}
            {form.hasFoundNothing && (
              <li className="list__item--dropdown list__item--dropdown-empty align-center">
                No contacts match your search.
              </li>
            )}
            {contactOptions.length === 0 && (
              <li className="list__item--dropdown list__item--dropdown-empty align-center">
                You haven’t created any contacts, yet!
              </li>
            )}
          </>
        </FilterDropdown>
      </div>
    </>
  )
}
